/**
 * WMO weather codes → a Dutch label and an icon.
 *
 * Open-Meteo reports the sky as a WMO code; the server passes it through
 * untouched and the client is the one place that turns it into something a
 * person reads. Icons are stroke SVGs in the app's house style (a shared
 * wrapper, `currentColor`), grouped by kind so a dozen codes share one drawing.
 */

export type WeatherKind = 'clear' | 'partly' | 'cloud' | 'fog' | 'rain' | 'snow' | 'thunder';

export function weatherKind(code: number): WeatherKind {
  if (code === 0 || code === 1) return 'clear';
  if (code === 2) return 'partly';
  if (code === 3) return 'cloud';
  if (code === 45 || code === 48) return 'fog';
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
  if (code >= 95) return 'thunder';
  return 'cloud';
}

const LABELS: Record<number, string> = {
  0: 'Onbewolkt',
  1: 'Overwegend helder',
  2: 'Half bewolkt',
  3: 'Bewolkt',
  45: 'Mist',
  48: 'Aanvriezende mist',
  51: 'Lichte motregen',
  53: 'Motregen',
  55: 'Dichte motregen',
  56: 'Lichte ijzel',
  57: 'IJzel',
  61: 'Lichte regen',
  63: 'Regen',
  65: 'Zware regen',
  66: 'Lichte onderkoelde regen',
  67: 'Onderkoelde regen',
  71: 'Lichte sneeuw',
  73: 'Sneeuw',
  75: 'Zware sneeuw',
  77: 'Sneeuwkorrels',
  80: 'Lichte buien',
  81: 'Buien',
  82: 'Zware buien',
  85: 'Lichte sneeuwbuien',
  86: 'Sneeuwbuien',
  95: 'Onweer',
  96: 'Onweer met hagel',
  99: 'Zwaar onweer met hagel',
};

const KIND_FALLBACK: Record<WeatherKind, string> = {
  clear: 'Onbewolkt',
  partly: 'Half bewolkt',
  cloud: 'Bewolkt',
  fog: 'Mist',
  rain: 'Regen',
  snow: 'Sneeuw',
  thunder: 'Onweer',
};

export function weatherLabel(code: number): string {
  return LABELS[code] ?? KIND_FALLBACK[weatherKind(code)];
}

/** Feather-derived cloud outline, reused by the cloudy conditions. */
const CLOUD = 'M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z';

function Svg({ children }: { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

/** A single raindrop, for the "coming hours" rain strip and summary line. */
export function DropIcon() {
  return (
    <Svg>
      <path d="M12 3.2c3.2 3.9 5.5 7 5.5 9.8a5.5 5.5 0 0 1-11 0c0-2.8 2.3-5.9 5.5-9.8z" />
    </Svg>
  );
}

/** Sun dipping below the horizon — the dashboard's "sun goes down" line. */
export function SunsetIcon() {
  return (
    <Svg>
      <path d="M17 18a5 5 0 0 0-10 0" />
      <path d="M12 9V3M4.5 10.5l1.4 1.4M1.5 18h2M20.5 18h2M18.1 11.9l1.4-1.4" />
      <path d="M16 5.5l-4 4-4-4" />
      <path d="M23 21.5H1" />
    </Svg>
  );
}

export function WeatherIcon({ code }: { code: number }) {
  switch (weatherKind(code)) {
    case 'clear':
      return (
        <Svg>
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.5v2.3M12 19.2v2.3M2.5 12h2.3M19.2 12h2.3M5.2 5.2l1.6 1.6M17.2 17.2l1.6 1.6M18.8 5.2l-1.6 1.6M6.8 17.2l-1.6 1.6" />
        </Svg>
      );
    case 'partly':
      return (
        <Svg>
          <circle cx="8" cy="7.5" r="3" />
          <path d="M8 1.8v1.4M2.3 7.5h1.4M4 3.5l1 1M12 3.5l-1 1" />
          <path d="M17.5 20h-8a3.6 3.6 0 0 1-.4-7.18 5 5 0 0 1 9.65 1.28A3.2 3.2 0 0 1 17.5 20z" />
        </Svg>
      );
    case 'fog':
      return (
        <Svg>
          <path d={CLOUD} />
          <path d="M6 20h12M8.5 23h9" />
        </Svg>
      );
    case 'rain':
      return (
        <Svg>
          <path d="M20 16.6A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
          <path d="M8 19v2M12 20v2M16 19v2" />
        </Svg>
      );
    case 'snow':
      return (
        <Svg>
          <path d="M20 16.6A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
          <path d="M8 20h.01M12 21h.01M16 20h.01M10 22.5h.01M14 22.5h.01" />
        </Svg>
      );
    case 'thunder':
      return (
        <Svg>
          <path d="M19 16.9A5 5 0 0 0 18 7h-1.26a8 8 0 1 0-11.62 9" />
          <path d="M13 11l-4 6h4l-2 5" />
        </Svg>
      );
    case 'cloud':
    default:
      return (
        <Svg>
          <path d={CLOUD} />
        </Svg>
      );
  }
}
